import { z } from "zod";

/**
 * BrandTheme / Brand* — allineati ai Pydantic schemas Phase 6
 * (`packages/dpanel-schemas/.../branding/__init__.py`).
 *
 * Theme JSON schema CHIUSO (HEX + font whitelist, no CSS arbitrario;
 * CLAUDE.md §8). `extra="forbid"` su tutte le request — qui replicato
 * con `.strict()`.
 */

export const BRAND_STATUSES = ["active", "inactive", "suspended"] as const;
export type BrandStatus = (typeof BRAND_STATUSES)[number];

export const BRAND_ASSET_KINDS = [
  "logo",
  "favicon",
  "login_background",
  "email_header",
] as const;
export type BrandAssetKind = (typeof BRAND_ASSET_KINDS)[number];

/** Font whitelist canonica (mirror del Pydantic FONT_FAMILY_WHITELIST). */
export const FONT_FAMILY_WHITELIST = [
  "system-ui",
  "sans-serif",
  "serif",
  "Inter, sans-serif",
  "Roboto, sans-serif",
  "IBM Plex Sans, sans-serif",
] as const;
export type FontFamily = (typeof FONT_FAMILY_WHITELIST)[number];

const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

/* ────────────────────────────────────────────── BrandTheme */

export const BrandThemeSchema = z
  .object({
    primary_color: z.string().regex(HEX_COLOR).nullable().optional(),
    secondary_color: z.string().regex(HEX_COLOR).nullable().optional(),
    background_color: z.string().regex(HEX_COLOR).nullable().optional(),
    text_color: z.string().regex(HEX_COLOR).nullable().optional(),
    font_family: z.enum(FONT_FAMILY_WHITELIST).nullable().optional(),
    border_radius_px: z.number().int().min(0).max(24).nullable().optional(),
    login_message: z.string().max(256).nullable().optional(),
  })
  .strict();

export type BrandTheme = z.infer<typeof BrandThemeSchema>;

export const BrandAssetUrlsSchema = z
  .object({
    logo_url: z.string().nullable().optional(),
    favicon_url: z.string().nullable().optional(),
    login_background_url: z.string().nullable().optional(),
    email_header_url: z.string().nullable().optional(),
  })
  .strict();

export type BrandAssetUrls = z.infer<typeof BrandAssetUrlsSchema>;

/* ────────────────────────────────────────────── BrandRead */

export const BrandReadSchema = z
  .object({
    id: z.guid(),
    name: z.string(),
    display_name: z.string().nullable().optional(),
    status: z.enum(BRAND_STATUSES),
    owner_id: z.guid().nullable(), // NULL = ownerless brand (ADR-0097)
    theme: BrandThemeSchema,
    asset_urls: BrandAssetUrlsSchema,
    primary_domain: z.string().nullable().optional(),
    // Phase 11.7: instance-wide "panel default" flag. At most one brand
    // may have this true. Backend BrandRead always includes the field
    // (defaults to false on brands not flagged); we tolerate absent for
    // forward-compat with older responses via .optional().
    is_panel_default: z.boolean().optional().default(false),
    smtp_configured: z.boolean(),
    created_by_email: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

export type BrandRead = z.infer<typeof BrandReadSchema>;

/* ────────────────────────────────────────────── PATCH /brands/{id}/panel-default */

export const BrandPanelDefaultRequestSchema = z
  .object({
    is_panel_default: z.boolean(),
  })
  .strict();

export type BrandPanelDefaultInput = z.infer<typeof BrandPanelDefaultRequestSchema>;

/* ────────────────────────────────────────────── BrandCreate / Update */

export const BrandCreateSchema = z
  .object({
    name: z.string().min(1).max(128),
    display_name: z.string().max(128).optional(),
    owner_id: z.guid(),
    theme: BrandThemeSchema,
  })
  .strict();

export type BrandCreateInput = z.infer<typeof BrandCreateSchema>;

export const BrandUpdateSchema = z
  .object({
    name: z.string().min(1).max(128).optional(),
    display_name: z.string().max(128).optional(),
    status: z.enum(BRAND_STATUSES).optional(),
    theme: BrandThemeSchema.optional(),
  })
  .strict();

export type BrandUpdateInput = z.infer<typeof BrandUpdateSchema>;

/* ────────────────────────────────────────────── /branding/current */

export const CurrentBrandingResponseSchema = z
  .object({
    brand_id: z.guid().nullable().optional(),
    brand_name: z.string().max(128),
    theme: BrandThemeSchema,
    asset_urls: BrandAssetUrlsSchema,
    is_default: z.boolean(),
    supported_languages: z.array(z.enum(["it", "en"])).default(["it", "en"]),
  })
  .passthrough();

export type CurrentBrandingResponse = z.infer<typeof CurrentBrandingResponseSchema>;

/* ────────────────────────────────────────────── Asset upload metadata */

export const BrandAssetUploadMetadataSchema = z
  .object({
    asset_kind: z.enum(BRAND_ASSET_KINDS),
    alt_text: z.string().max(256).optional(),
  })
  .strict();

export type BrandAssetUploadMetadata = z.infer<
  typeof BrandAssetUploadMetadataSchema
>;

/* ────────────────────────────────────────────── List filters */

export const BrandsListFiltersSchema = z
  .object({
    owner_id: z.guid().optional(),
    status: z.enum(BRAND_STATUSES).optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(200).default(50),
  })
  .passthrough();

export type BrandsListFilters = z.infer<typeof BrandsListFiltersSchema>;

/* ────────────────────────────────────────────── Theme defaults */

/**
 * Tema DPanel di default — mirror canonical del backend
 * `apps/api/.../branding/service.py:DEFAULT_DPANEL_THEME`. Phase 11.5
 * fix: pre-fix i 2 default erano divergenti (FE #5B2A86 viola, BE
 * #1A4E8A blu) → confusione visiva sulla /branding empty-state preview
 * vs chrome applicato a runtime.
 *
 * Single source of truth lato FE: questo costante DEVE corrispondere
 * al BE `DEFAULT_DPANEL_THEME`. Drift check manuale (no test integration
 * cross-language oggi).
 *
 * Usato per:
 * - Baseline ThemeEditor quando crei un nuovo brand
 * - Read-only BrandPreview sull'empty state /branding (Phase 11.5)
 * - Fallback applyBrandTheme se `/branding/current` non risponde
 */
export const DEFAULT_THEME: BrandTheme = {
  primary_color: "#1A4E8A",
  secondary_color: "#3C7DBC",
  background_color: "#F4F6F8",
  text_color: "#1F2933",
  font_family: "system-ui",
  border_radius_px: 8,
  login_message: "Welcome to DPanel",
};

/* ────────────────────────────────────────────── SMTP config (ADR-0087) */

export const SMTP_SECURITIES = ["starttls", "tls", "none"] as const;
export type SmtpSecurity = (typeof SMTP_SECURITIES)[number];

/**
 * GET /brands/{id}/smtp-config response. Mirror del Pydantic
 * `BrandSmtpConfigRead`. Password SEMPRE mascherata (`********` se configurata,
 * `""` altrimenti) — il segreto vive in OpenBao KV, mai esposto (ADR-0087).
 * `.passthrough()` su response per forward-compat (ADR-0073).
 */
export const BrandSmtpConfigReadSchema = z
  .object({
    brand_id: z.guid(),
    smtp_host: z.string(),
    smtp_port: z.number().int().min(1).max(65535),
    smtp_security: z.enum(SMTP_SECURITIES),
    from_address: z.string(),
    from_name: z.string().nullable().optional(),
    smtp_username: z.string().nullable().optional(),
    smtp_password_masked: z.enum(["********", ""]),
    dkim_configured: z.boolean(),
    configured: z.boolean(),
    active: z.boolean(),
    updated_at: z.string(),
  })
  .passthrough();

export type BrandSmtpConfigRead = z.infer<typeof BrandSmtpConfigReadSchema>;

/**
 * PATCH /brands/{id}/smtp-config request. Mirror del Pydantic
 * `BrandSmtpConfigUpdateRequest` (`extra="forbid"` → `.strict()`).
 * Merge-patch: ogni campo opzionale. `smtp_password` plaintext SOLO in input
 * (inviata via TLS, mai persistita in DB; va in OpenBao KV — ADR-0087).
 * Su prima creazione `smtp_host` + `from_address` sono richiesti (validato BE).
 */
export const BrandSmtpConfigUpdateSchema = z
  .object({
    smtp_host: z.string().min(1).max(253).optional(),
    smtp_port: z.number().int().min(1).max(65535).optional(),
    smtp_security: z.enum(SMTP_SECURITIES).optional(),
    from_address: z.string().email().optional(),
    from_name: z.string().max(128).nullable().optional(),
    smtp_username: z.string().max(256).nullable().optional(),
    smtp_password: z.string().min(1).optional(),
    active: z.boolean().optional(),
  })
  .strict();

export type BrandSmtpConfigUpdateInput = z.infer<typeof BrandSmtpConfigUpdateSchema>;

/**
 * POST /brands/{id}/smtp-config/test request/response (ADR-0087).
 * Usa la config salvata; il body porta solo il destinatario.
 */
export const SMTP_TEST_OUTCOMES = [
  "success",
  "connection_failed",
  "authentication_failed",
  "tls_failed",
] as const;
export type SmtpTestOutcome = (typeof SMTP_TEST_OUTCOMES)[number];

export const BrandSmtpTestSchema = z
  .object({
    to_address: z.string().email(),
  })
  .strict();

export type BrandSmtpTestInput = z.infer<typeof BrandSmtpTestSchema>;

export const BrandSmtpTestResultSchema = z
  .object({
    outcome: z.enum(SMTP_TEST_OUTCOMES),
    detail: z.string(),
    tested_at: z.string(),
  })
  .passthrough();

export type BrandSmtpTestResult = z.infer<typeof BrandSmtpTestResultSchema>;
